import React from 'react';
import { AIRecommendation, AIRecommendationActions } from './types';
import { debugWarn } from '../../utils/debug';

type AISuggestionCardProps = {
  stepKey?: string;
  recommendation: AIRecommendation;
  actions?: AIRecommendationActions;
  onPrimary?: (actionType: AIRecommendationActions['primaryType']) => void;
  onSecondary?: (actionType: AIRecommendationActions['secondaryType']) => void;
  onRedirect?: () => void;
};

const AISuggestionCard: React.FC<AISuggestionCardProps> = ({
  stepKey,
  recommendation,
  actions,
  onPrimary,
  onSecondary,
  onRedirect,
}) => {
  const confidenceLabel = recommendation.confidence || 'MEDIUM';
  const isRequestMoreInfo = recommendation.action === 'ASK_DETAILS';
  const guidanceText =
    recommendation.mode === 'AT_FAULT'
      ? recommendation.summary || ''
      : recommendation.advisoryText || '';
  const resolvedStepKey = stepKey || recommendation.triggerStepKey || 'unknown';
  const bullets = recommendation.bullets ?? [];
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-emerald-900">
            {recommendation.mode === 'AT_FAULT' ? 'Mitigation tips' : 'AI review'}
          </p>
          <p className="text-xs text-emerald-700">
            {recommendation.mode === 'NOT_AT_FAULT' && recommendation.fit
              ? `Fit: ${recommendation.fit} · `
              : ''}
            Confidence: {confidenceLabel}
          </p>
        </div>
        <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
          AI guidance
        </span>
      </div>
      {guidanceText && <p className="mt-3 text-sm text-emerald-900">{guidanceText}</p>}
      {bullets.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-emerald-700 space-y-1">
          {bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      {(actions || (recommendation.mode === 'NOT_AT_FAULT' && recommendation.offerRedirectToAtFault && onRedirect)) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {actions && (
            <button
              type="button"
              className="btn-primary"
              onMouseDown={
                actions.primaryType === 'ASK_DETAILS'
                  ? () => {
                      debugWarn('[ai-recs][ui] Add details mousedown', { stepKey: resolvedStepKey, ts: Date.now() });
                    }
                  : undefined
              }
              onClick={(event) => {
                if (actions.primaryType === 'ASK_DETAILS') {
                  event.preventDefault();
                  event.stopPropagation();
                }
                onPrimary?.(actions.primaryType);
              }}
            >
              {actions.primaryLabel}
            </button>
          )}
          {actions?.secondaryLabel && actions.secondaryType && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onSecondary?.(actions.secondaryType)}
            >
              {actions.secondaryLabel}
            </button>
          )}
          {recommendation.mode === 'NOT_AT_FAULT' &&
            recommendation.offerRedirectToAtFault &&
            onRedirect && (
              <button type="button" className="btn-secondary" onClick={onRedirect}>
                Switch to reduction path
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default AISuggestionCard;
